import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api } from "@/api/client";
import { errorMessage } from "@/api/errors";

export interface Organization {
  id: string;
  name: string;
  emailDomain: string;
  status: "ACTIVE" | "DISABLED";
  createdAt: string;
}

interface Props {
  org: Organization;
  onClose: () => void;
  onUpdated?: (next: Organization) => void;
}

export default function EditOrganizationDialog({ org, onClose, onUpdated }: Props) {
  const qc = useQueryClient();
  const [name, setName] = useState(org.name);
  const [emailDomain, setEmailDomain] = useState(org.emailDomain);

  const save = useMutation({
    mutationFn: async () => {
      const body: { name?: string; emailDomain?: string } = {};
      if (name.trim() !== org.name) body.name = name.trim();
      if (emailDomain.trim().toLowerCase() !== org.emailDomain) body.emailDomain = emailDomain.trim();
      if (!body.name && !body.emailDomain) throw new Error("没有修改");
      const { data, error, response } = await api.PATCH(
        "/api/v1/organizations/{id}" as never,
        { params: { path: { id: org.id } }, body } as never
      );
      if (error || !response.ok) throw error || new Error("保存失败");
      return data as unknown as Organization;
    },
    onSuccess: (next) => {
      qc.invalidateQueries({ queryKey: ["organizations"] });
      qc.invalidateQueries({ queryKey: ["organization", org.id] });
      onUpdated?.(next);
      toast.success("已保存");
      onClose();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="bg-background rounded-lg border border-border w-full max-w-md shadow-lg">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <h2 className="font-semibold">编辑公司</h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">✕</button>
        </div>
        <form
          onSubmit={(e) => { e.preventDefault(); save.mutate(); }}
          className="p-5 space-y-4 text-sm"
        >
          <label className="block space-y-1.5">
            <span className="text-xs font-medium">名称</span>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
            />
          </label>
          <label className="block space-y-1.5">
            <span className="text-xs font-medium">邮箱域名</span>
            <input
              value={emailDomain}
              onChange={(e) => setEmailDomain(e.target.value)}
              required
              pattern="^[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}$"
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm font-mono"
            />
            <span className="text-xs text-muted-foreground">改了之后,后续注册该域名的用户会归入此公司</span>
          </label>
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-border px-4 py-2 text-sm hover:bg-muted"
            >
              取消
            </button>
            <button
              type="submit"
              disabled={save.isPending}
              className="rounded-md bg-primary text-primary-foreground px-4 py-2 text-sm font-medium hover:opacity-90 disabled:opacity-50"
            >
              {save.isPending ? "保存中…" : "保存"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
